import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailCard } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailCard";
import { DetailDates } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailDates";
import { DetailLoader } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailLoader";
import { DetailRow } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailRow";
import { DetailValueChartSection } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailValueChartSection";
import { RelatedSectionRedesign } from "web_myplann/Portfolio/Components/Redesign/Detail/RelatedSectionRedesign";
import { FundListSection } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/FundListSection";
import { PortfolioBasicInfoRows } from "web_myplann/Portfolio/Components/Redesign/Detail/Sections/PortfolioBasicInfoRows";
import { getPaidAllLogic } from "web_myplann/Portfolio/Helpers/getPaidAllLogic";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import { PortfolioItemKind } from "web_myplann/Portfolio/Types/portfolioItemKind";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

export const BankProductRedesign: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
	onUpdateValue,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailLoader hasChart />;
	}

	const { paidAllValue, shouldShowPaidAll } = getPaidAllLogic({
		baseData,
		customPaidAllValue: baseData.bankInstructionMonthlyPayment ?? 0,
	});

	const paidAll = paidAllValue
		? `${moneyFormatter(
				paidAllValue,
				currencySign,
				undefined,
				0,
			)} ${t(Resources.PortfolioDetail.SharedLabels.Frequency.Monthly).toLowerCase()}`
		: EMPTY_DETAIL_FIELD;

	return (
		<>
			<DetailValueChartSection
				reporting={reporting}
				currencySign={currencySign}
				onEditValue={onUpdateValue}
			/>

			<DetailCard label={t(Resources.DataCard.BasicInfo.title)}>
				<PortfolioBasicInfoRows baseData={baseData} />
				{shouldShowPaidAll && (
					<DetailRow
						label={t(Resources.PortfolioDetail.SharedLabels.paidAll)}
						value={paidAll}
					/>
				)}
			</DetailCard>
			<DetailDates data={baseData} />

			<FundListSection
				portfolioId={baseData.id}
				title={t(Resources.PortfolioDetail.ToolHistory.BankProducts.title)}
				tools={normalizeTools(baseData.tools)}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				currencySign={currencySign}
			/>

			<RelatedSectionRedesign
				documents={documents?.data}
				related={baseData.related}
				readOnly={baseData.archived}
				itemKind={PortfolioItemKind.Portfolio}
				portfolioId={baseData.id}
			/>
		</>
	);
};
